import React from "react";
import { evaluatePickAgainstScore } from "@/lib/pickStatus";

const LIVE_CODES = new Set([
    "1H",
    "HT",
    "2H",
    "ET",
    "BT",
    "P",
    "SUSP",
    "INT",
    "LIVE",
]);
const FINISHED_CODES = new Set(["FT", "AET", "PEN"]);

const STATUS_STYLE = {
    on_track: {
        live: "ON TRACK",
        finished: "WON",
        cls: "text-primary border-primary/40 bg-primary/10",
    },
    slipping: {
        live: "SLIPPING",
        finished: "LOST",
        cls: "text-red-300 border-red-500/40 bg-red-500/10",
    },
    tight: {
        live: "PUSH",
        finished: "VOID",
        cls: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    },
};

/**
 * Tiny "what's happening right now" strip for a prediction card.
 *
 * Renders nothing if we have no live row at all, or if the match hasn't
 * kicked off yet (kickoff time is already shown elsewhere). Once the match
 * goes live or finishes, we display the score + state. When the bet is
 * still in play, we also light up an "ON TRACK" / "SLIPPING" badge so users
 * can see at a glance whether their slip is winning right now.
 */
export default function LiveScoreStrip({ pick, live, testId }) {
    if (!live) return null;
    const short = (live.status?.short || "").toUpperCase();
    const elapsed = live.status?.elapsed;
    const home = live.goals?.home;
    const away = live.goals?.away;
    const isLive = LIVE_CODES.has(short);
    const isFinished = FINISHED_CODES.has(short);
    if (!isLive && !isFinished) return null;
    if (home == null || away == null) return null;

    const label = isLive
        ? short === "HT"
            ? "HT"
            : elapsed != null
            ? `LIVE ${elapsed}'`
            : "LIVE"
        : short; // FT / AET / PEN

    // Live games show ON TRACK / SLIPPING / PUSH; finished games show WON /
    // LOST / VOID immediately (the evaluator will officially settle later).
    const betStatus = evaluatePickAgainstScore(pick, home, away);
    const styleRow = betStatus ? STATUS_STYLE[betStatus] : null;
    const badgeLabel = styleRow
        ? isLive
            ? styleRow.live
            : styleRow.finished
        : null;

    return (
        <div
            className={`border-t border-border mt-3 pt-3 space-y-2 ${
                isLive ? "" : "opacity-80"
            }`}
            data-testid={testId}
        >
            <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest">
                    {isLive && (
                        <span
                            className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"
                            aria-hidden="true"
                        />
                    )}
                    <span
                        className={isLive ? "text-red-400" : "text-zinc-500"}
                        data-testid={`${testId}-status`}
                    >
                        {label}
                    </span>
                </span>
                <span
                    className="font-mono text-base font-bold text-white tabular-nums"
                    data-testid={`${testId}-score`}
                >
                    {home} <span className="text-zinc-600">–</span> {away}
                </span>
            </div>
            {badgeLabel && (
                <div className="flex justify-end">
                    <span
                        className={`inline-block font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${styleRow.cls}`}
                        data-testid={`${testId}-bet-status`}
                    >
                        {badgeLabel}
                    </span>
                </div>
            )}
        </div>
    );
}
